import { Iframe, PublicShareLayout } from '../../lib/components/editor';
import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { gql, useQuery } from '@apollo/client';
import { context as contextResolver } from '../../lib/graphql/context';
import { initializeApollo, addApolloState } from '../../lib/graphql/apollo-ssr';
import { Meta, P } from '../../lib/ui';
import GmailImage from '../../images/gmail.png';
import { useAuth } from '../../lib/auth'

type Props = { brandSettings; shareId?: string };

const GET_SHARE_QUERY = gql`
  query GetShare($id: ID) {
    getShare(id: $id) {
      id
      name
      rawTemplate
      css
    }
  }
`;

const GET_BRAND_SETTING = gql`
  query GetBrandSettings {
    getBrandSettings {
      logo
      companyName
      brandColor
    }
  }
`;

const isServer = typeof window === 'undefined';

const SharePage = (pageProps: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const shareId = (pageProps.shareId ?? router.query.id) as string | undefined;
  const { loading, data } = useQuery(GET_SHARE_QUERY, {
    ssr: true,
    skip: !router.isReady || !shareId,
    variables: {
      id: shareId,
    },
    fetchPolicy: 'cache-first',
    returnPartialData: true,
  });
  const { loading: fetchBrandSetting, data: brandSettingData } = useQuery(GET_BRAND_SETTING, {
    ssr: false,
    skip: !user,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const brandSettings = useMemo(() => {
    if (brandSettingData?.getBrandSettings) {
      return brandSettingData?.getBrandSettings
    }
  }, [brandSettingData]);
  const isLoading = (!data?.getShare && loading) || fetchBrandSetting;
  const templateName = isLoading
    ? data?.getShare?.name || 'loading'
    : data?.getShare?.name || 'Shared Template Not Found';
  const cssVal = data?.getShare?.css || '';
  const htmlVal = data?.getShare?.rawTemplate || '';
  const hasContent = data?.getShare?.rawTemplate != null;
  return (
    <PublicShareLayout subtitle={templateName}>
      <Meta title={templateName ? templateName : 'Shared Template'} />
      <div className="bg-postdrop-light h-full w-full flex-auto">
        {hasContent ? (
          <div className="share-preview-container flex justify-between flex-col xl:flex-row">
            <div className="flex justify-center">
              <div className="desktop-preview my-10 flex flex-col justify-top">
                <img
                  src={GmailImage}
                  alt="Gmail"
                  className="desktop-preview__client"
                />
                <Iframe
                  onlyFrame
                  brandSettings={brandSettings}
                  src={htmlVal}
                  css={cssVal}
                  id="desktop-preview"
                  sandbox=""
                  frameBorder="0"
                  className="desktop-preview__iframe"
                  title={templateName}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <div className="mobile-preview flex justify-center my-10 flex-col justify-center">
                <img
                  src="/img/iphone.png"
                  alt="iPhone Client"
                  className="mobile-preview__client"
                />
                <Iframe
                  onlyFrame
                  src={htmlVal}
                  brandSettings={brandSettings}
                  css={cssVal}
                  frameBorder="0"
                  id="mobile-preview"
                  sandbox=""
                  className="mobile-preview__iframe"
                  title={templateName}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="col-12 text-center my-3">
            <p>Shared template not found.</p>
          </div>
        )}
        <div className="flex mb-8 justify-center">
          <P>
            Email previews powered by{' '}
            <Link
              href="https://postdrop.io"
              className="font-medium underline hover:no-underline">
              
                Postdrop
              
            </Link>
          </P>
        </div>
      </div>
    </PublicShareLayout>
  );
};

SharePage.requireLogin = false;

export async function getServerSideProps(ctx) {
  const apolloClient = initializeApollo(null, contextResolver(ctx, false));

  try {
    await apolloClient.query({
      query: GET_SHARE_QUERY,
      variables: {
        id: ctx.query.id,
      },
    });

    return addApolloState(apolloClient, {
      props: { shareId: ctx.query.id as string },
    });
  } catch (error) {
    return {
      props: { shareId: ctx.query.id as string },
    };
  }
}

export default SharePage;
